import React, { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getNotifications } from "../../redux/slices/MediaSlice";
import axiosClient from "../../axios-client";
import Drawer from "@mui/material/Drawer";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Skeleton from "@mui/material/Skeleton";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import "./Notification.css";
import { Badge, Popover } from "@mui/material";
import { useSocket } from "../../contexts/SocketContext";
import { useSnackbar } from "../ExplorerModule/SnackbarProvider";
import NotificationPopover from "../PopoverNotify";
import { formatUsDate } from "../../utils/OtherUtils";

const Notification = ({ open, onClose, bellRef }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { socket } = useSocket();
  const { notifications, notificationsLoading, hasMoreNotifications } =
    useSelector((state) => state.media);
  const isLogin = useSelector((state) => state.user.isLogin);
  const userInfo = useSelector((state) => state.user.userInfo);
  const user = isLogin ? JSON.parse(userInfo) : null;
  const userId = user?.UserID;
  const [tabValue, setTabValue] = useState(0);
  // ── Orders states ────────────────────────────────────────
  const [orderNotify, setOrderNotify] = useState({
    totalOrders: 0,
    allOrders: [],
    loadingOrder: false, // initial + tab change load
    errorOrder: null,
  });
  const { showSnackbar } = useSnackbar();
  const [orderPage, setOrderPage] = useState(1);
  const [hasMoreOrders, setHasMoreOrders] = useState(true);
  const [loadingMoreOrders, setLoadingMoreOrders] = useState(false);
  const [expanded, setExpanded] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [latestOrder, setLatestOrder] = useState(null);

  useEffect(() => {
    if (!socket) return;
    let autoHideTimer;
    const handleReceiveOrder = (data) => {
      const { vendorId, order } = data;
      if (vendorId != userId) return;

      setLatestOrder(order);
      setAnchorEl(bellRef.current);

      // ✅ AUTO HIDE POPOVER AFTER 5 SECONDS
      autoHideTimer = setTimeout(() => {
        setAnchorEl(null);
      }, 10000);

      setOrderNotify((prev) => ({
        ...prev,
        allOrders: [order, ...prev.allOrders],
        totalOrders: prev.totalOrders + 1,
      }));

      showSnackbar({
        message: `📢 New order #${order.Id} received`,
        severity: "success",
        actionLabel: "View",
        onAction: () => navigate(`/dashboardpage/orders?orderId=${order.Id}`),
      });
    };

    socket.on("receive-order", handleReceiveOrder);

    return () => {
      socket.off("receive-order", handleReceiveOrder);
      clearTimeout(autoHideTimer); // 🧹 cleanup
    };
  }, [socket, userId, bellRef]);

  // ─── Reset & Initial Fetch when drawer opens or tab changes ────────
  // Trigger both fetches when drawer opens
  useEffect(() => {
    if (!userId || !open) return;
    dispatch(
      getNotifications({
        userId,
        Seen: 1,
        pageSize: 10,
        pageId: 1,
      }),
    );
    setOrderPage(1);
    setOrderNotify({
      totalOrders: 0,
      allOrders: [],
      loadingOrder: true,
      errorOrder: null,
    });
    setHasMoreOrders(true);
    setLoadingMoreOrders(false);
    setTabValue(0);
    setExpanded([]);
  }, [dispatch, userId, open]);

  // ─── Load Orders (initial + load more) ─────────────────────────────
  useEffect(() => {
    if (!userId || orderPage < 1) return;
    const fetchOrdersData = async () => {
      const isFirstPage = orderPage === 1;
      if (isFirstPage) {
        setOrderNotify((prev) => ({
          ...prev,
          loadingOrder: true,
          errorOrder: null,
        }));
      } else {
        setLoadingMoreOrders(true);
      }
      try {
        const response = await axiosClient.put(`order/ListAllOrder`, {
          pageId: orderPage,
          pageSize: 10,
          orderStatus: "PENDING",
          paymentStatus: "COMPLETED",
          ownerId: userId,
        });
        const newOrders = response.data?.Data || [];
        const total = response.data?.totalRecords || 0;
        const hasMore = newOrders.length === 10; // ← most reliable default
        setOrderNotify((prev) => ({
          ...prev,
          totalOrders: total,
          allOrders: isFirstPage
            ? newOrders
            : [...prev.allOrders, ...newOrders],
          loadingOrder: false,
        }));
        setHasMoreOrders(hasMore);
      } catch (error) {
        console.error("Orders fetch error:", error);
        setOrderNotify((prev) => ({
          ...prev,
          errorOrder: error.message || "Failed to load orders",
          loadingOrder: false,
        }));
        setHasMoreOrders(false);
      } finally {
        setLoadingMoreOrders(false);
      }
    };

    fetchOrdersData();
  }, [userId, tabValue, orderPage]); // Removed orderNotify.allOrders.length — no longer needed with new logic
  useEffect(() => {
    if (tabValue !== 0) return;
    setExpanded((prev) => {
      const len = notifications.length;
      let next = [...prev];
      while (next.length < len) next.push(false);
      if (next.length > len) next = next.slice(0, len);
      return next;
    });
  }, [notifications.length, tabValue]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // ─── Media Load More ────────────────────────────────────────
  const loadMoreMedia = useCallback(() => {
    if (!hasMoreNotifications || notificationsLoading) return;
    const nextPage = Math.floor(notifications.length / 10) + 1;
    dispatch(
      getNotifications({
        userId,
        Seen: 1,
        pageSize: 10,
        pageId: nextPage,
      }),
    );
  }, [
    dispatch,
    userId,
    notifications.length,
    hasMoreNotifications,
    notificationsLoading,
  ]);

  // ─── Orders Load More ───────────────────────────────────────
  const loadMoreOrders = useCallback(() => {
    if (!hasMoreOrders || loadingMoreOrders || orderNotify.loadingOrder) return;
    setOrderPage((prev) => prev + 1);
  }, [hasMoreOrders, loadingMoreOrders, orderNotify.loadingOrder]);

  const handleNavigation = useCallback(
    (vendorId, imageId) => {
      navigate(`/enlargeImageView?vendorid=${vendorId}&imageid=${imageId}`);
      onClose();
    },
    [navigate, onClose],
  );

  const toggleExpanded = useCallback((index) => {
    setExpanded((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  }, []);

  // ─── Media Render ───────────────────────────────────────────
  const renderMediaNotifications = () => {
    if (notificationsLoading && notifications.length === 0) {
      return [...Array(6)].map((_, i) => (
        <Box
          key={i}
          className="notif-item"
          sx={{ display: "flex", gap: 2, p: 1.5 }}
        >
          <Skeleton variant="circular" width={40} height={40} />
          <Box flex={1}>
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="40%" sx={{ mt: 0.5 }} />
          </Box>
          <Skeleton
            variant="rectangular"
            width={40}
            height={40}
            sx={{ borderRadius: 1 }}
          />
        </Box>
      ));
    }

    if (notifications.length === 0) {
      return (
        <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
          No notifications yet
        </Typography>
      );
    }

    return notifications.map((item, index) => {
      const fullName = `${item.firstName} ${item.lastName}`;
      let action = "interacted with your post";
      if (item.notifyType === "like") action = "liked your post";
      if (item.notifyType === "comment")
        action = `commented: "${item.content}"`;

      const timeDiff = Math.floor(
        (Date.now() - new Date(item.createdAt)) / 60000,
      );
      const time =
        timeDiff < 1
          ? "Just now"
          : timeDiff < 60
            ? `${timeDiff}m ago`
            : timeDiff < 1440
              ? `${Math.floor(timeDiff / 60)}h ago`
              : `${Math.floor(timeDiff / 1440)}d ago`;

      const fullText = `${fullName} ${action}`;
      const isLong = fullText.length > 45;
      const isExp = expanded[index] ?? false;
      const displayAction =
        isLong && !isExp
          ? action.slice(0, 32 - fullName.length) + "..."
          : action;

      return (
        <Box
          key={item.notifyId}
          className="notif-item"
          onClick={() => handleNavigation(item.createdBy, item.imageId)}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            p: 1.5,
            borderBottom: "1px solid #eee",
            cursor: "pointer",
            "&:hover": { bgcolor: "#f5faff" },
          }}
        >
          <Avatar
            src={
              item.userPhoto ||
              "https://st4.depositphotos.com/6672868/20448/v/600/depositphotos_204485038-stock-illustration-user-vector-icon-simple-illustration.jpg"
            }
            alt="avatar"
            sx={{ width: 40, height: 40 }}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "baseline",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="body2" fontWeight={500} component="span">
                {fullName}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                component="span"
              >
                {displayAction}
              </Typography>
              {isLong && (
                <Typography
                  variant="caption"
                  color="primary"
                  sx={{ cursor: "pointer", fontWeight: 500 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpanded(index);
                  }}
                >
                  {isExp ? "Less" : "More"}
                </Typography>
              )}
            </Box>
            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ mt: 0.5, display: "block" }}
            >
              {time}
            </Typography>
          </Box>
          {item.imageUrl && (
            <Avatar
              src={item.imageUrl}
              variant="rounded"
              sx={{ width: 44, height: 44, mt: 0.5 }}
            />
          )}
        </Box>
      );
    });
  };

  // ─── Orders Render ──────────────────────────────────────────
  const renderOrders = () => {
    if (orderNotify.loadingOrder && orderNotify.allOrders.length === 0) {
      return [...Array(4)].map((_, i) => (
        <Box key={i} sx={{ display: "flex", gap: 2, p: 2 }}>
          <Skeleton variant="circular" width={56} height={56} />
          <Box flex={1}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="40%" sx={{ mt: 1 }} />
            <Skeleton variant="text" width="50%" sx={{ mt: 1 }} />
          </Box>
        </Box>
      ));
    }

    if (orderNotify.errorOrder) {
      return (
        <Typography color="error" align="center" sx={{ py: 4 }}>
          {orderNotify.errorOrder}
        </Typography>
      );
    }

    if (orderNotify.allOrders.length === 0) {
      return (
        <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
          <Typography variant="h6" gutterBottom>
            New Orders
          </Typography>
          <Typography>No new orders at the moment.</Typography>
        </Box>
      );
    }
    // const formatDate = (dateInput) => {
    //   if (!dateInput) return "N/A";
    //   const date = new Date(dateInput);
    //   if (isNaN(date.getTime())) return "N/A";
    //   const formatter = new Intl.DateTimeFormat("en-GB", {
    //     day: "2-digit",
    //     month: "short",
    //     year: "numeric",
    //   });
    //   return formatter.format(date).replace(/ /g, " - ");
    // };
    return (
      <Box sx={{ p: 1 }}>
        {orderNotify.allOrders.map((order) => {
          let serviceImage = null;
          try {
            const photos = JSON.parse(order.servicePhoto || "[]");
            serviceImage = photos[0] || null; // first photo
          } catch (e) {
            console.log("Error parsing servicePhoto:", e);
          }

          return (
            <Box
              key={order.Id}
              onClick={() => {
                navigate(`/dashboardpage/orders?orderId=${order.Id}`);
                onClose();
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                mx: 1,
                my: 0.5,
                borderRadius: 2,
                border: "1px solid #e0e0e0",
                backgroundColor: "white",
                cursor: "pointer",
                transition: "all 0.15s ease",
                "&:hover": {
                  backgroundColor: "#f8f9ff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                  borderColor: "#d0d0ff",
                },
              }}
            >
              <Avatar
                src={serviceImage || "/default-service.jpg"} // fallback image
                variant="rounded"
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: "4px",
                  bgcolor: serviceImage ? "transparent" : "action.hover",
                }}
              />

              {/* Order Info */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" fontWeight={400} noWrap>
                  Order #{order.Id} •{" "}
                  <span style={{ color: "green" }}>
                    {order.paymentStatus == "COMPLETED" ? "Paid" : "Unpaid"}
                  </span>
                </Typography>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {order.serviceName || order.categoryName || "Service"}
                </Typography>

                <Box
                  component="div"
                  sx={{
                    mt: 0.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    fontSize: "0.85rem",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {formatUsDate(order.reserveDate) || "N/A"}
                  </Typography>

                  <Typography variant="body2" fontWeight={500}>
                    ₹{order.totalAmount}
                  </Typography>
                </Box>

                <Typography variant="caption" color="text.secondary">
                  Customer: {order.userName || "—"}
                </Typography>
              </Box>

              {/* Arrow / indicator */}
              <Box sx={{ color: "action.active", fontSize: "1.4rem" }}>→</Box>
            </Box>
          );
        })}

        {/* Load More Section */}
        {hasMoreOrders && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography
              component="button"
              variant="body2"
              color="primary"
              onClick={loadMoreOrders}
              disabled={loadingMoreOrders}
              sx={{
                bgcolor: "#e3f2fd",
                px: 4,
                py: 1.2,
                borderRadius: 20,
                cursor: "pointer",
                border: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#bbdefb" },
              }}
            >
              {loadingMoreOrders ? "Loading..." : "Show more"}
            </Typography>
          </Box>
        )}

        {!hasMoreOrders && orderNotify.allOrders.length > 0 && (
          <Typography
            variant="body2"
            color="text.secondary"
            align="center"
            sx={{ py: 4 }}
          >
            No more orders to show
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <>
      {/* <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        disableRestoreFocus
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Box sx={{ p: 2, maxWidth: 260 }}>
          <Typography fontWeight={600}> New Order Recived </Typography>
          <Typography variant="body2">Order #{latestOrder?.Id}</Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            onClick={() =>
              navigate(`/dashboardpage/orders?orderId=${latestOrder?.Id}`)
            }
          >
            Click to view details
          </Typography>
        </Box>
      </Popover> */}
      <NotificationPopover
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        title={`New Order Received For ${latestOrder?.Service}`}
        subtitle={`Order #${latestOrder?.Id}`}
        onActionClick={() =>
          navigate(`/dashboardpage/orders?orderId=${latestOrder?.Id}`)
        }
        actionLabel="View Order Details"
        autoCloseDelay={6000}
      />
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: { width: { xs: "90%", sm: 380 }, maxWidth: 420 },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid #e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "#fafafa",
            }}
          >
            <Typography variant="h6">Notifications</Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab
              label={
                <Badge
                  badgeContent={notifications.length}
                  color="error"
                  invisible={notifications.length === 0}
                >
                  Media Notifications
                </Badge>
              }
            />
            <Tab
              label={
                <Badge
                  badgeContent={orderNotify.totalOrders}
                  color="error"
                  invisible={orderNotify.totalOrders === 0}
                >
                  New Orders
                </Badge>
              }
            />
          </Tabs>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {tabValue === 0 && (
              <>
                {renderMediaNotifications()}
                {hasMoreNotifications && notifications.length > 0 && (
                  <Box sx={{ p: 2, textAlign: "center" }}>
                    <Typography
                      component="button"
                      variant="body2"
                      color="primary"
                      onClick={loadMoreMedia}
                      disabled={notificationsLoading}
                      sx={{
                        bgcolor: "#e3f2fd",
                        px: 3,
                        py: 1,
                        borderRadius: 20,
                        cursor: "pointer",
                        border: "none",
                        fontWeight: 500,
                      }}
                    >
                      {notificationsLoading ? "Loading..." : "Show more"}
                    </Typography>
                  </Box>
                )}

                {!hasMoreNotifications &&
                  notifications.length > 0 &&
                  !notificationsLoading && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      align="center"
                      sx={{ py: 3 }}
                    >
                      No more notifications
                    </Typography>
                  )}
              </>
            )}

            {tabValue === 1 && renderOrders()}
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default Notification;
